import React, { useEffect, useMemo, useState } from "react"
import {
    View,
    StyleSheet,
    Text,
    TouchableOpacity,
    ActivityIndicator,
} from "react-native"
import StageDragList from "./StageDragList"
import FunnelGoalBadge from "@common/components/funnel/FunnelGoalBadge"
import FunnelFadeUp from "@common/components/funnel/FunnelFadeUp"
import FunnelTypewriter from "@common/components/funnel/FunnelTypewriter"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"
import { SUBJECT_GOALS } from "@common/components/funnel/funnelSubjectGoals"

/**
 * Le programme d'un stage vendu AU CRÉNEAU, retouchable.
 *
 * Il se lit **par matière**, pas par horaire : on ne s'engage pas sur « le
 * français est samedi à 10h », qui n'est pas une promesse qu'on tient.
 *
 * Et les blocs ne sont PAS numérotés. La composition répartit une matière sur
 * toutes les séances du week-end : numéroter ferait lire « Maths sur 3
 * créneaux » là où le compositeur vient d'annoncer « Maths · 2 créneaux ». Le
 * volume se dit au compositeur, le contenu se lit ici — deux comptes affichés
 * finiraient toujours par se contredire.
 *
 * **Un bloc par matière, et un seul.** Les chapitres d'une matière se suivent
 * sans césure, et « Ajouter un chapitre » est TOUT EN BAS. Découper le bloc par
 * créneau faisait apparaître un bouton d'ajout entre deux chapitres : le parent
 * y lisait une frontière qui ne veut rien dire pour lui — la répartition sur
 * les créneaux, il l'a déjà faite au compositeur.
 *
 * **L'ordre se change en tirant la poignée.** C'est le geste attendu sur une
 * liste qu'on lit comme une progression ; le nouvel ordre est réécrit sur les
 * créneaux de la matière, dans leur ordre de calendrier.
 *
 * Trois actions bornées par la classe : remplacer, retirer, ajouter. Ce sont
 * les BORNES qui décident lesquelles apparaissent — `retirer` s'éteint au
 * plancher, `ajouter` au plafond — si bien qu'un lycéen (1 à 1) ne voit que
 * « remplacer » sans qu'aucun cas particulier ne soit codé.
 *
 * L'objectif visé revient en étiquette sur la matière : c'est là qu'on rend au
 * parent le choix qu'il vient de faire au compositeur.
 *
 * Pourquoi un composant à part et non une option de {@see SummerProgramCalendar} :
 * ce dernier sert l'été, le brevet et les stages à la semaine en LECTURE SEULE.
 * Y greffer des actions ferait porter à trois produits le risque d'un quatrième.
 *
 * La poignée d'édition reste la SÉANCE (programme, jour) — c'est elle que
 * l'API réécrit. Le regroupement par matière est une lecture, pas un modèle :
 * `seance_start` continue de rassembler les deux chapitres d'un créneau de 2 h.
 *
 * Purement présentationnel — l'état et les appels vivent dans StagePlanScreen.
 */
/** Un bloc de matière s'écrit après l'autre : le temps de lire le premier. */
const BLOCK_STAGGER = 1100

const StageEditableProgram = ({
    program,
    bounds,
    savingSeanceKey,
    subjectGoals = {},
    onRemove,
    onAdd,
    onReorder,
}) => {
    // Dérivé de `program` seul : ne se recalcule pas à chaque coup de pouce
    // (chargement d'une séance, rendu du parent).
    // L'entrée en cascade ne joue qu'à la révélation du programme : une ligne
    // ajoutée après coup se pose tout de suite (sinon elle occupe sa place,
    // invisible, le temps d'un délai prévu pour la cascade).
    const [revealed, setRevealed] = useState(false)
    useEffect(() => {
        const t = setTimeout(() => setRevealed(true), 4200)
        return () => clearTimeout(t)
    }, [])

    const subjectBlocks = useMemo(() => {
        const weeks = program?.schedule ?? []

        // Le pivot : on rassemble d'abord par SÉANCE — c'est la poignée d'édition,
        // et les deux chapitres d'un créneau de 2 h partagent son `seance_start` —
        // puis on regroupe ces séances PAR MATIÈRE pour la lecture.
        const seances = []
        const byKey = {}
        weeks.forEach((week) => {
            const entries = week.sessions ?? []
            entries.forEach((entry) => {
                const key = entry.seance_start ?? `${entry.date}T${entry.time}`
                if (!byKey[key]) {
                    byKey[key] = {
                        key,
                        date: entry.date,
                        time: entry.time,
                        subjects: [],
                        sessionStart: entry.session_start,
                        studyProgramId: entry.study_program_id,
                        day: entry.day,
                        chapters: [],
                    }
                    seances.push(byKey[key])
                }
                if (
                    entry.subject?.id &&
                    !byKey[key].subjects.some((s) => s.id === entry.subject.id)
                ) {
                    byKey[key].subjects.push({
                        id: entry.subject.id,
                        name: entry.subject.name,
                    })
                }
                byKey[key].chapters.push({
                    ...entry.chapter,
                    subjectId: entry.subject?.id,
                    subjectName: entry.subject?.name,
                })
            })
        })

        // Une matière porte ses chapitres à la SUITE, dans l'ordre du calendrier —
        // le parent lit « ce qu'on fait en maths », pas « ce qu'il y a samedi ». Le
        // créneau reste attaché à chaque ligne, parce que c'est lui que l'API
        // réécrit ; il ne se voit simplement plus.
        const subjectBlocks = []
        const bySubject = {}
        seances.forEach((seance) => {
            seance.chapters.forEach((chapter) => {
                const id = chapter.subjectId ?? "sans-matiere"
                if (!bySubject[id]) {
                    bySubject[id] = {
                        id,
                        name: chapter.subjectName ?? "Programme",
                        rows: [],
                        addSeance: null,
                    }
                    subjectBlocks.push(bySubject[id])
                }
                bySubject[id].rows.push({ seance, chapter })
            })
        })
        // Les créneaux que porte chaque matière, dans l'ordre du calendrier :
        // dès qu'il y en a deux, ils se nomment (« Créneau 1 », « Créneau 2 »)
        // — c'est ce qui rend lisible qu'un chapitre ne se retire pas quand
        // son créneau est au plancher.
        subjectBlocks.forEach((block) => {
            block.seanceKeys = [
                ...new Set(block.rows.map((row) => row.seance.key)),
            ]
        })

        // « Ajouter un chapitre » vise le PREMIER créneau de la matière qui n'a pas
        // atteint son plafond. Un seul bouton, en bas : le parent demande un
        // chapitre de plus, l'écran choisit où il tient.
        subjectBlocks.forEach((block) => {
            const seen = new Set()
            block.addSeance =
                block.rows
                    .map((row) => row.seance)
                    .filter((seance) => {
                        if (seen.has(seance.key)) return false
                        seen.add(seance.key)
                        return true
                    })
                    .find((seance) => seance.chapters.length < bounds.max) ??
                null
        })

        return subjectBlocks
    }, [program, bounds.max])

    if (!subjectBlocks.length) {
        return (
            <Text style={styles.illustrativeNote}>
                Le programme détaillé arrive — votre tuteur le personnalise dès
                la 1ʳᵉ séance.
            </Text>
        )
    }

    return (
        <View style={styles.list}>
            {subjectBlocks.map((block, blockIndex) => {
                const goal = SUBJECT_GOALS.find(
                    (g) => g.slug === subjectGoals[String(block.id)]
                )

                return (
                    <FunnelFadeUp
                        key={String(block.id)}
                        delay={blockIndex * BLOCK_STAGGER}
                        style={styles.card}
                    >
                        <View style={styles.subjectHeader}>
                            <FunnelTypewriter
                                text={block.name}
                                delay={blockIndex * BLOCK_STAGGER + 260}
                                style={styles.subjectName}
                            />
                            {goal ? (
                                <FunnelFadeUp
                                    delay={
                                        blockIndex * BLOCK_STAGGER +
                                        260 +
                                        block.name.length * 42
                                    }
                                >
                                    <FunnelGoalBadge
                                        goal={goal}
                                        size="sm"
                                        fillStars
                                        delay={
                                            blockIndex * BLOCK_STAGGER +
                                            500 +
                                            block.name.length * 42
                                        }
                                    />
                                </FunnelFadeUp>
                            ) : null}
                        </View>

                        {/* Une seule liste : les chapitres de la matière, dans
                            l'ordre du calendrier, tous créneaux confondus. */}
                        <StageDragList
                            entranceDelay={
                                revealed
                                    ? null
                                    : blockIndex * BLOCK_STAGGER +
                                      700 +
                                      block.name.length * 42
                            }
                            items={block.rows}
                            keyExtractor={(row) => String(row.chapter.id)}
                            disabled={
                                savingSeanceKey !== null ||
                                block.rows.length < 2
                            }
                            onReorder={(from, to) => onReorder(block, from, to)}
                            renderItem={(
                                row,
                                index,
                                { dragging, handleProps }
                            ) => {
                                const { seance, chapter } = row
                                const saving = savingSeanceKey === seance.key
                                // Les bornes se lisent sur la SÉANCE entière,
                                // pas sur ce que cette matière y pose : c'est
                                // la séance que l'API réécrit.
                                const canRemove =
                                    seance.chapters.length > bounds.min
                                const namedSeances = block.seanceKeys.length > 1
                                const firstOfSeance =
                                    index === 0 ||
                                    block.rows[index - 1].seance.key !==
                                        seance.key

                                return (
                                    <View>
                                        {namedSeances && firstOfSeance ? (
                                            <Text style={styles.seanceTitle}>
                                                Créneau{" "}
                                                {block.seanceKeys.indexOf(
                                                    seance.key
                                                ) + 1}
                                                {seance.chapters.length <=
                                                bounds.min
                                                    ? ` · ${bounds.min} chapitre${bounds.min > 1 ? "s" : ""} minimum`
                                                    : ""}
                                            </Text>
                                        ) : null}
                                        <View
                                            style={[
                                                styles.chapterRow,
                                                dragging &&
                                                    styles.chapterRowDragging,
                                            ]}
                                        >
                                            {block.rows.length > 1 ? (
                                                <View
                                                    style={styles.handle}
                                                    {...handleProps}
                                                    accessibilityRole="button"
                                                    accessibilityLabel={`Déplacer ${chapter.name}`}
                                                >
                                                    <FontAwesome5
                                                        name="grip-lines"
                                                        size={11}
                                                        color={
                                                            theme.colors.light
                                                        }
                                                    />
                                                </View>
                                            ) : null}
                                            <View style={styles.chapterTexts}>
                                                <Text
                                                    style={styles.chapterName}
                                                    numberOfLines={2}
                                                >
                                                    {chapter.name}
                                                </Text>
                                                {chapter.theme ? (
                                                    <Text
                                                        style={
                                                            styles.chapterTheme
                                                        }
                                                        numberOfLines={1}
                                                    >
                                                        {chapter.theme}
                                                    </Text>
                                                ) : null}
                                            </View>
                                            {/* Un mot, pas une icône : « Supprimer » se
                                            comprend sans légende. Remplacer = supprimer
                                            puis ajouter — une action de moins à apprendre. */}
                                            <TouchableOpacity
                                                style={[
                                                    styles.action,
                                                    !canRemove &&
                                                        styles.actionDisabled,
                                                ]}
                                                disabled={!canRemove || saving}
                                                onPress={() =>
                                                    onRemove(seance, chapter)
                                                }
                                                accessibilityRole="button"
                                                accessibilityLabel={`Supprimer le chapitre ${chapter.name}`}
                                            >
                                                <Text style={styles.actionText}>
                                                    Supprimer
                                                </Text>
                                            </TouchableOpacity>
                                        </View>
                                    </View>
                                )
                            }}
                        />
                        {block.seanceKeys.length === 1 &&
                        block.rows.some(
                            (row) => row.seance.chapters.length <= bounds.min
                        ) ? (
                            <Text style={styles.floorHint}>
                                Un créneau garde au moins {bounds.min} chapitre
                                {bounds.min > 1 ? "s" : ""}.
                            </Text>
                        ) : null}

                        {block.rows.some(
                            (row) => row.seance.key === savingSeanceKey
                        ) ? (
                            <View style={styles.savingRow}>
                                <ActivityIndicator
                                    size="small"
                                    color={theme.colors.primary}
                                />
                            </View>
                        ) : block.addSeance ? (
                            <TouchableOpacity
                                style={styles.addButton}
                                activeOpacity={0.8}
                                onPress={() => onAdd(block, block.addSeance)}
                                accessibilityRole="button"
                                accessibilityLabel={`Ajouter un chapitre de ${block.name}`}
                            >
                                <FontAwesome5
                                    name="plus"
                                    size={10}
                                    color={theme.colors.primary}
                                />
                                <Text style={styles.addText}>
                                    Ajouter un chapitre
                                </Text>
                            </TouchableOpacity>
                        ) : null}
                    </FunnelFadeUp>
                )
            })}
        </View>
    )
}

const styles = StyleSheet.create({
    list: { gap: 14 },

    card: {
        borderWidth: 2,
        borderColor: theme.colors.lightMore,
        borderRadius: 18,
        backgroundColor: theme.colors.white,
        paddingHorizontal: 16,
        paddingTop: 14,
        paddingBottom: 12,
    },

    // La matière commande la lecture : son nom porte le poids, son objectif
    // revient à droite — c'est là qu'on rend au parent le choix qu'il a fait.
    subjectHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        paddingBottom: 12,
        borderBottomWidth: 1.5,
        borderBottomColor: theme.colors.lightMore,
        marginBottom: 12,
    },
    subjectName: {
        flexShrink: 1,
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 17,
        color: theme.colors.primaryDark || theme.colors.primary,
    },
    chapterRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingVertical: 11,
        paddingHorizontal: 12,
        borderRadius: 12,
        backgroundColor: theme.colors.greyBg,
        marginBottom: 8,
    },
    chapterRowDragging: {
        backgroundColor: theme.colors.white,
        borderWidth: 1.5,
        borderColor: theme.colors.primary,
    },
    // Une poignée discrète : elle se voit quand on la cherche, elle ne dispute
    // pas la lecture au titre du chapitre.
    handle: {
        width: 22,
        alignItems: "center",
        justifyContent: "center",
        alignSelf: "stretch",
        marginLeft: -4,
    },
    chapterTexts: { flex: 1 },
    chapterName: {
        fontFamily: "Montserrat-Bold",
        fontSize: 14,
        color: theme.colors.black,
        lineHeight: 19,
    },
    chapterTheme: {
        fontFamily: "Montserrat-Medium",
        fontSize: 11.5,
        color: theme.colors.grey,
        marginTop: 2,
    },

    actions: { flexDirection: "row", gap: 7 },
    seanceTitle: {
        fontFamily: "Montserrat-Bold",
        fontSize: 10.5,
        color: theme.colors.grey,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginTop: 8,
        marginBottom: 6,
        marginLeft: 2,
    },
    floorHint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.grey,
        marginTop: 6,
        marginLeft: 2,
    },
    actionText: {
        fontFamily: "Montserrat-SemiBold",
        fontSize: 12,
        color: theme.colors.grey,
    },
    action: {
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 8,
        backgroundColor: theme.colors.white,
        alignItems: "center",
        justifyContent: "center",
    },
    actionDisabled: { opacity: 0.35 },

    savingRow: { paddingVertical: 10, alignItems: "center" },
    addButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        paddingVertical: 11,
        borderRadius: 12,
        borderWidth: 1.5,
        borderStyle: "dashed",
        borderColor: theme.colors.primary,
        marginBottom: 4,
    },
    addText: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.primary,
    },

    illustrativeNote: {
        fontFamily: "Montserrat-Medium",
        fontSize: 13,
        color: theme.colors.grey,
        lineHeight: 19,
    },
})

export default StageEditableProgram
